/**
 * Mermaid Tool - UI 工具函數
 * static/js/mermaid/ui.js
 */

// ============== DOM 工具 ==============

/**
 * XSS 防護：轉義 HTML 特殊字元
 * @param {string} text - 原始文字
 * @returns {string} 轉義後的文字
 */
export function escapeHtml(text) {
    if (!text) return '';
    const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };
    return String(text).replace(/[&<>"']/g, char => map[char]);
}

/**
 * 獲取 DOM 元素
 * @param {string} selector - CSS 選擇器
 * @param {HTMLElement} [parent=document] - 父元素
 * @returns {HTMLElement|null}
 */
export function $(selector, parent = document) {
    return parent.querySelector(selector);
}

/**
 * 獲取所有符合的 DOM 元素
 * @param {string} selector - CSS 選擇器
 * @param {HTMLElement} [parent=document] - 父元素
 * @returns {HTMLElement[]}
 */
export function $$(selector, parent = document) {
    return Array.from(parent.querySelectorAll(selector));
}

// ============== 格式化工具 ==============

/**
 * 計算相對時間
 * @param {number} diffMs - 毫秒差值
 * @returns {string|null} 相對時間字串，超過 7 天返回 null
 */
export function getRelativeTime(diffMs) {
    if (diffMs < 0) return null;
    
    const MINUTE = 60000;
    const HOUR = 3600000;
    const DAY = 86400000;
    const WEEK = 604800000;
    
    if (diffMs < MINUTE) return '剛才';
    if (diffMs < HOUR) return Math.floor(diffMs / MINUTE) + ' 分鐘前';
    if (diffMs < DAY) return Math.floor(diffMs / HOUR) + ' 小時前';
    if (diffMs < WEEK) return Math.floor(diffMs / DAY) + ' 天前';
    
    return null;
}

/**
 * 格式化日期時間
 * @param {string|Date} dateStr - ISO 日期字串或 Date 物件
 * @returns {string} 格式化後的日期
 */
export function formatDate(dateStr) {
    if (!dateStr) return '';
    
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }
    
    const now = new Date();
    const diff = now - date;
    
    const relative = getRelativeTime(diff);
    if (relative) return relative;
    
    return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
}

// ============== 複製功能 ==============

/**
 * 複製文字到剪貼簿
 * @param {string} text - 要複製的文字
 * @returns {Promise<boolean>} 是否成功
 */
export async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        return copyToClipboardFallback(text);
    }
}

/**
 * 複製到剪貼簿的回退方案
 * @param {string} text - 要複製的文字
 * @returns {boolean} 是否成功
 */
export function copyToClipboardFallback(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
        document.execCommand('copy');
        return true;
    } catch (e) {
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
}

// ============== Toast 通知 ==============

let toastContainer = null;

/**
 * 獲取或建立 toast 容器
 * @returns {HTMLElement}
 */
function getToastContainer() {
    if (!toastContainer) {
        toastContainer = document.getElementById('toastContainer');
        if (!toastContainer) {
            toastContainer = document.createElement('div');
            toastContainer.id = 'toastContainer';
            toastContainer.className = 'toast-container position-fixed bottom-0 end-0 p-3';
            toastContainer.style.zIndex = '1100';
            document.body.appendChild(toastContainer);
        }
    }
    return toastContainer;
}

/**
 * 獲取 Toast 的 CSS class
 * @param {string} type - 類型
 * @returns {{ bgClass: string, textClass: string }}
 */
export function getToastClasses(type) {
    const bgClass = {
        success: 'bg-success',
        warning: 'bg-warning',
        danger: 'bg-danger',
        error: 'bg-danger',
        info: 'bg-info'
    }[type] || 'bg-info';
    
    const textClass = type === 'warning' ? 'text-dark' : 'text-white';
    
    return { bgClass, textClass };
}

/**
 * 顯示 Toast 通知
 * @param {string} message - 訊息內容
 * @param {string} type - 類型 ('success', 'warning', 'danger', 'info')
 * @param {number} duration - 顯示時間（毫秒）
 */
export function showToast(message, type = 'info', duration = 3000) {
    const container = getToastContainer();
    if (!container) return;
    
    const toastId = 'toast-' + Date.now();
    const { bgClass, textClass } = getToastClasses(type);
    
    const toastHtml = `
        <div id="${toastId}" class="toast align-items-center ${bgClass} ${textClass} border-0" role="alert">
            <div class="d-flex">
                <div class="toast-body">${escapeHtml(message)}</div>
                <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
            </div>
        </div>
    `;
    
    container.insertAdjacentHTML('beforeend', toastHtml);
    
    const toastElement = document.getElementById(toastId);
    if (!toastElement) return;
    
    try {
        const toast = new bootstrap.Toast(toastElement, { delay: duration });
        toast.show();
        
        toastElement.addEventListener('hidden.bs.toast', () => {
            toastElement.remove();
        });
    } catch (e) {
        // Bootstrap not available, auto-remove after duration
        setTimeout(() => toastElement?.remove(), duration);
    }
}

// ============== QR Code ==============

/**
 * 生成 QR Code
 * @param {string} containerId - 容器元素 ID
 * @param {string} url - 要編碼的 URL
 * @param {number} size - QR 碼大小
 */
export function generateQRCode(containerId, url, size = 200) {
    const container = document.getElementById(containerId);
    if (!container) return;
    
    container.innerHTML = '';
    
    if (typeof QRCode !== 'undefined') {
        new QRCode(container, {
            text: url,
            width: size,
            height: size,
            colorDark: '#000000',
            colorLight: '#ffffff',
            correctLevel: QRCode.CorrectLevel.M
        });
    } else {
        container.innerHTML = '<p class="text-muted small">QR Code 功能不可用</p>';
    }
}

// ============== Modal 工具 ==============

/**
 * 顯示 Bootstrap Modal
 * @param {string} modalId - Modal 元素 ID
 */
export function showModal(modalId) {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
        const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
        modal.show();
    }
}

/**
 * 隱藏 Bootstrap Modal
 * @param {string} modalId - Modal 元素 ID
 */
export function hideModal(modalId) {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
        const modal = bootstrap.Modal.getInstance(modalElement);
        if (modal) {
            modal.hide();
        }
    }
}

// ============== 確認對話框 ==============

/**
 * 顯示確認對話框
 * @param {string} message - 確認訊息
 * @returns {boolean} 用戶是否確認
 */
export function confirm(message) {
    return window.confirm(message);
}

// ============== 渲染狀態更新 ==============

/**
 * 更新渲染狀態顯示
 * @param {string} status - 狀態 ('ready', 'rendering', 'error')
 * @param {string} [message] - 狀態訊息
 */
export function updateRenderStatus(status, message = '') {
    const statusElement = document.getElementById('renderStatus');
    if (!statusElement) return;
    
    const statusConfig = {
        ready: { class: 'bg-success', text: '準備就緒' },
        rendering: { class: 'bg-warning text-dark', text: '渲染中...' },
        error: { class: 'bg-danger', text: message || '渲染錯誤' }
    };
    
    const config = statusConfig[status] || statusConfig.ready;
    
    statusElement.className = `badge ${config.class}`;
    statusElement.textContent = config.text;
}
